import { Loader2 } from 'lucide-react';
import type { ArtifactsOwner } from './artifacts.types';
import { useArtifacts } from './useArtifacts';
import { ArtifactsList } from './components/ArtifactsList';
import { ArtifactsEmptyState } from './components/ArtifactsEmptyState';
import { ArtifactPanelOverlay } from './components/ArtifactPanelOverlay';

/** Owner-specific copy — the only thing that differs between contexts. */
const DESCRIPTION: Record<ArtifactsOwner['type'], string> = {
  startup: 'Personas, problem statements, journey maps and other generated artifacts.',
  scope: 'Personas, problem statements, journey maps and other artifacts generated for this scope.',
};

interface ArtifactsModuleProps {
  owner: ArtifactsOwner;
  /** Optional test id on the loader (kept for existing scope e2e). */
  loaderTestId?: string;
}

/**
 * Artifacts feature module. The same list + editor panel renders for a
 * startup and for a scope; `owner` drives which endpoints run.
 */
export function ArtifactsModule({ owner, loaderTestId }: ArtifactsModuleProps): JSX.Element {
  const {
    artifacts,
    isLoading,
    deletingId,
    activeArtifact,
    openArtifact,
    closeArtifact,
    deleteArtifact,
    saveActiveArtifact,
    saveStructuredActiveArtifact,
    shareActiveArtifact,
  } = useArtifacts(owner);

  return (
    <section className="mx-auto w-full max-w-5xl px-6 py-10">
      <header className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Artifacts</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{DESCRIPTION[owner.type]}</p>
      </header>

      <div className="bg-surface rounded-2xl border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
        {isLoading ? (
          <div className="flex items-center justify-center py-16" data-testid={loaderTestId}>
            <Loader2 className="w-6 h-6 text-gray-300 dark:text-gray-500 animate-spin" />
          </div>
        ) : artifacts.length === 0 ? (
          <ArtifactsEmptyState />
        ) : (
          <ArtifactsList
            artifacts={artifacts}
            deletingId={deletingId}
            onOpen={openArtifact}
            onDelete={deleteArtifact}
          />
        )}
      </div>

      {activeArtifact && (
        <ArtifactPanelOverlay
          artifact={activeArtifact}
          onClose={closeArtifact}
          onSave={saveActiveArtifact}
          onSaveStructured={saveStructuredActiveArtifact}
          onShare={shareActiveArtifact}
        />
      )}
    </section>
  );
}
